/**
 * Supabase Key Validator
 * 
 * Validates that Supabase keys are correctly configured and prevents
 * service role keys from being used in client-side code.
 * 
 * SECURITY: Service role keys have elevated privileges and must NEVER
 * be exposed in client-side code (VITE_ prefixed variables).
 */

/**
 * Decode JWT payload without verification (for key type detection)
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
function decodeJWT(token) {
  if (!token || typeof token !== 'string') {
    return null;
  }

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const padded = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decoded = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Check if a Supabase key is a service role key
 * @param {string} key - Supabase API key
 * @returns {boolean} True if the key is a service role key
 */
export function isServiceRoleKey(key) {
  if (!key) return false;
  
  const decoded = decodeJWT(key);
  if (!decoded) return false;
  
  // Service role keys have role: "service_role" in the JWT payload
  return decoded.role === 'service_role';
}

/**
 * Check if a Supabase key is an anon key
 * @param {string} key - Supabase API key
 * @returns {boolean} True if the key is an anon key
 */
export function isAnonKey(key) {
  if (!key) return false;
  
  const decoded = decodeJWT(key);
  if (!decoded) return false;
  
  // Anon keys have role: "anon" in the JWT payload
  return decoded.role === 'anon';
}

/**
 * Validate that a key is an anon key (not a service role key)
 * Throws an error if the key is a service role key
 * @param {string} key - Supabase API key to validate
 * @param {string} keyName - Name of the key variable (for error messages)
 * @throws {Error} If the key is a service role key
 */
export function validateAnonKey(key, keyName = 'VITE_SUPABASE_ANON_KEY') {
  if (!key) {
    throw new Error(`${keyName} is not set`);
  }

  if (isServiceRoleKey(key)) {
    throw new Error(
      `SECURITY ERROR: ${keyName} contains a SERVICE ROLE KEY instead of an anon key. ` +
      `Service role keys have elevated privileges and must NEVER be exposed in client-side code. ` +
      `Please update your .env.local file with the correct anon key from Supabase Dashboard → Settings → API → Project API keys → anon public key.`
    );
  }

  if (!isAnonKey(key)) {
    console.warn(
      `Warning: ${keyName} does not appear to be a valid anon key. ` +
      `The JWT payload role is not "anon". Please verify you're using the correct key.`
    );
  }

  return true;
}

